// Number helpers that reproduce the JVM's rounding and parsing, so the web prints exactly the
// figures the Android app does (and the tests ported from it pin them).

/** JavaScript's Math.round, written out: half up, so the apps and the mockup round alike. */
export function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5);
}

/** Kotlin's Int.mod: the remainder with the divisor's sign, so -1 mod 360 is 359. */
export function floorMod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

/** Kotlin's Double.mod: like [floorMod] but leaves -0 as it is (callers add 0 to clear it). */
export function doubleMod(value: number, divisor: number): number {
  const r = value % divisor;
  return r !== 0 && Math.sign(r) !== Math.sign(divisor) ? r + divisor : r;
}

/** Java's "%0Nd": zero-padded to [width], the sign counting towards it. */
export function padInteger(value: number, width: number): string {
  if (value < 0) return `-${String(-value).padStart(width - 1, '0')}`;
  return String(value).padStart(width, '0');
}

function incrementDigits(digits: string): string {
  const chars = digits.split('');
  for (let i = chars.length - 1; i >= 0; i--) {
    if (chars[i] !== '9') {
      chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1);
      return chars.join('');
    }
    chars[i] = '0';
  }
  return `1${chars.join('')}`;
}

/**
 * Java's String.format("%.Nf") — which Android uses for every decimal figure. It rounds half up
 * on the shortest decimal form of the double, where toFixed rounds the exact binary value: 1005 m
 * is "1.01 km" on Android and would be "1.00 km" with toFixed.
 */
export function formatFixed(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return String(value);
  const negative = value < 0 || Object.is(value, -0);
  const [mantissa, exponent] = Math.abs(value).toExponential().split('e');
  const digits = mantissa.replace('.', '');
  // value = 0.<digits> × 10^pointAt; keep the digits down to the last decimal place.
  const keep = Number(exponent) + 1 + decimals;
  let kept: string;
  if (keep < 0) {
    kept = '0';
  } else if (keep === 0) {
    kept = digits[0] >= '5' ? '1' : '0';
  } else {
    kept = digits.slice(0, keep).padEnd(keep, '0');
    if ((digits[keep] ?? '0') >= '5') kept = incrementDigits(kept);
  }
  const padded = kept.padStart(decimals + 1, '0');
  const whole = padded.slice(0, padded.length - decimals);
  const text = decimals > 0 ? `${whole}.${padded.slice(padded.length - decimals)}` : whole;
  return negative ? `-${text}` : text;
}

/**
 * A number typed with either decimal separator, or null. Digits and at most one separator only —
 * no exponent, no spaces inside — and, when [signed], one leading "-" or "+".
 */
export function parseDecimal(text: string, signed = false): number | null {
  let cleaned = text.trim().replace(/,/g, '.');
  let sign = 1;
  if (signed && cleaned.length > 0 && (cleaned[0] === '-' || cleaned[0] === '+')) {
    if (cleaned[0] === '-') sign = -1;
    cleaned = cleaned.substring(1);
  }
  if (cleaned.length === 0 || cleaned.split('.').length > 2) return null;
  if (!/^[0-9.]+$/.test(cleaned)) return null;
  // Number('.') is NaN and 400 nines is Infinity: neither is a finite number.
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return sign * value;
}
